import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const WorkflowFilters = ({ 
  filters, 
  onFiltersChange, 
  onNewJob, 
  onAssignOperator, 
  onBulkAction 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const designOptions = [
    { value: 'all', label: 'All Designs' },
    { value: 'floral-summer', label: 'Floral Summer Collection' },
    { value: 'geometric-modern', label: 'Geometric Modern' },
    { value: 'vintage-classic', label: 'Vintage Classic' },
    { value: 'abstract-art', label: 'Abstract Art Series' },
    { value: 'nature-inspired', label: 'Nature Inspired' }
  ];

  const operatorOptions = [
    { value: 'all', label: 'All Operators' },
    { value: 'rajesh-kumar', label: 'Rajesh Kumar' },
    { value: 'priya-sharma', label: 'Priya Sharma' },
    { value: 'amit-patel', label: 'Amit Patel' },
    { value: 'sunita-devi', label: 'Sunita Devi' },
    { value: 'vikram-singh', label: 'Vikram Singh' }
  ];

  const priorityOptions = [
    { value: 'all', label: 'All Priorities' },
    { value: 'high', label: 'High Priority' },
    { value: 'medium', label: 'Medium Priority' },
    { value: 'low', label: 'Low Priority' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'pending', label: 'Pending' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'on-hold', label: 'On Hold' }
  ];

  const handleFilterChange = (key, value) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      {/* Header Row */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Icon name="Filter" size={20} color="var(--color-primary)" />
            <h3 className="font-semibold text-foreground">Production Filters</h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            iconSize={16}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? 'Less Filters' : 'More Filters'}
          </Button>
        </div>

        {/* Quick Actions */}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="default"
            size="sm"
            iconName="Plus"
            iconSize={16}
            onClick={onNewJob}
          >
            New Production Job
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="Users"
            iconSize={16}
            onClick={onAssignOperator}
          >
            Assign Operator
          </Button>
          <Button
            variant="ghost"
            size="sm"
            iconName="Download"
            iconSize={16}
          >
            Export Report
          </Button>
        </div>
      </div>

      {/* Basic Filters Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Input
          type="date"
          label="From Date"
          value={filters.fromDate}
          onChange={(e) => handleFilterChange('fromDate', e.target.value)}
          className="w-full"
        />
        <Input
          type="date"
          label="To Date"
          value={filters.toDate}
          onChange={(e) => handleFilterChange('toDate', e.target.value)}
          className="w-full"
        />
        <Select
          label="Design"
          options={designOptions}
          value={filters.design}
          onChange={(value) => handleFilterChange('design', value)}
          searchable
        />
        <Select
          label="Priority"
          options={priorityOptions}
          value={filters.priority}
          onChange={(value) => handleFilterChange('priority', value)}
        />
      </div>

      {/* Expanded Filters */}
      {isExpanded && (
        <div className="border-t border-border pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <Select
              label="Assigned Operator"
              options={operatorOptions}
              value={filters.operator}
              onChange={(value) => handleFilterChange('operator', value)}
              searchable
            />
            <Select
              label="Status"
              options={statusOptions}
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
            />
            <Input
              type="text"
              label="Job ID"
              placeholder="Search by job ID..."
              value={filters.jobId}
              onChange={(e) => handleFilterChange('jobId', e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Input
              type="number"
              label="Min Fabric (meters)"
              placeholder="0"
              value={filters.minFabric}
              onChange={(e) => handleFilterChange('minFabric', e.target.value)}
            />
            <Input
              type="number"
              label="Max Fabric (meters)"
              placeholder="1000"
              value={filters.maxFabric}
              onChange={(e) => handleFilterChange('maxFabric', e.target.value)}
            />
            <Input
              type="number"
              label="Min Pieces"
              placeholder="0"
              value={filters.minPieces}
              onChange={(e) => handleFilterChange('minPieces', e.target.value)}
            />
            <Input
              type="number"
              label="Max Pieces"
              placeholder="1000"
              value={filters.maxPieces}
              onChange={(e) => handleFilterChange('maxPieces', e.target.value)}
            />
          </div>
        </div>
      )}

      {/* Active Filters & Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4 border-t border-border">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">Active filters:</span>
          {Object.entries(filters).map(([key, value]) => {
            if (!value || value === 'all' || value === '') return null;
            return (
              <div key={key} className="flex items-center space-x-1 bg-primary/10 text-primary px-2 py-1 rounded text-xs">
                <span>{key}: {value}</span>
                <button
                  onClick={() => handleFilterChange(key, key.includes('Date') ? '' : 'all')}
                  className="hover:bg-primary/20 rounded p-0.5"
                >
                  <Icon name="X" size={12} />
                </button>
              </div>
            );
          })}
          {Object.values(filters).some(value => value && value !== 'all' && value !== '') && (
            <Button
              variant="ghost"
              size="xs"
              iconName="X"
              iconSize={12}
              onClick={() => onFiltersChange({
                fromDate: '',
                toDate: '',
                design: 'all',
                operator: 'all',
                priority: 'all',
                status: 'all',
                jobId: '',
                minFabric: '',
                maxFabric: '',
                minPieces: '',
                maxPieces: ''
              })}
            >
              Clear All
            </Button>
          )}
        </div>

        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            iconName="RefreshCw"
            iconSize={16}
            onClick={() => window.location.reload()}
          >
            Refresh
          </Button>
          <div className="text-sm text-muted-foreground">
            Last updated: {new Date().toLocaleTimeString()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowFilters;